import { Media, MediaSize } from '../api/usersApi';
import config from '../config/config';

/** Базовый URL для медиа (без /api/v1, /api/v2) — статика отдаётся с корня */
function getMediaBaseUrl(): string {
  const base = config.api.baseURL.replace(/\/api\/v1\/?$/, '').replace(/\/api\/v2\/?$/, '').replace(/\/$/, '');
  return base || '';
}

/**
 * Получить URL медиа нужного размера.
 * Сервер отдаёт пути вида /media/avatar_xxx.webp — склеиваем с базой API.
 */
export function getMediaUrl(
  media: Media | null | undefined,
  preferredSize: 'thumbnail' | 'small' | 'medium' | 'large' | 'original' | 'avatar' | 'full' = 'medium'
): string | null {
  if (!media?.sizes?.length) {
    return null;
  }

  const apiBaseUrl = getMediaBaseUrl();

  // Ищем нужный размер
  const size = media.sizes.find(s => s.sizeType === preferredSize);
  if (size) {
    const path = (size.url || '').trim();
    if (!path) return null;
    return path.startsWith('http') ? path : `${apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  // Если нужного размера нет, используем fallback логику
  // Для новых размеров: avatar -> full -> large -> medium -> small -> thumbnail
  // Для старых размеров: original -> large -> medium -> small -> thumbnail
  let fallbackOrder: MediaSize['sizeType'][];
  
  if (preferredSize === 'avatar') {
    fallbackOrder = ['full', 'large', 'medium', 'small', 'thumbnail'];
  } else if (preferredSize === 'full') {
    fallbackOrder = ['avatar', 'large', 'medium', 'small', 'thumbnail'];
  } else {
    // Старая логика для обратной совместимости
    const sizeOrder: MediaSize['sizeType'][] = ['thumbnail', 'small', 'medium', 'large', 'original'];
    const preferredIndex = sizeOrder.indexOf(preferredSize);
    fallbackOrder = sizeOrder.slice(preferredIndex + 1);
  }
  
  for (const fallbackSize of fallbackOrder) {
    const found = media.sizes.find(s => s.sizeType === fallbackSize);
    if (found) {
      const path = (found.url || '').trim();
      if (!path) continue;
      return path.startsWith('http') ? path : `${apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
    }
  }

  // Если ничего не нашли, берём первый доступный
  const firstSize = media.sizes[0];
  const path = (firstSize.url || '').trim();
  if (!path) return null;
  return path.startsWith('http') ? path : `${apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
}

export interface PostImageUrls {
  thumbUrl: string;
  fullUrl: string;
  hasProgressive: boolean;
}

/**
 * Для поста возвращает массивы URL по индексу: сначала миниатюра (avatar), потом full.
 * Используется для прогрессивной загрузки в ленте.
 */
export function getPostImageUrls(images: Media[] | null | undefined): PostImageUrls[] {
  if (!images?.length) return [];
  return images.map((img) => {
    const thumbUrl = getMediaUrl(img, 'avatar') || getMediaUrl(img, 'full') || '';
    const fullUrl = getMediaUrl(img, 'full') || getMediaUrl(img, 'avatar') || '';
    const hasProgressive = !!(thumbUrl && fullUrl && thumbUrl !== fullUrl);
    return { thumbUrl, fullUrl, hasProgressive };
  }).filter((x) => x.thumbUrl || x.fullUrl);
}

/**
 * Получить URL аватарки пользователя (для обратной совместимости)
 */
export function getAvatarUrl(
  media: Media | null | undefined,
  preferredSize: 'thumbnail' | 'small' | 'medium' | 'large' | 'original' | 'avatar' | 'full' = 'medium'
): string | null {
  return getMediaUrl(media, preferredSize);
}

/**
 * Получить URL медиа по ID
 */
export async function getMediaUrlById(
  mediaId: number | null | undefined,
  preferredSize: 'thumbnail' | 'small' | 'medium' | 'large' | 'original' | 'avatar' | 'full' = 'medium'
): Promise<string | null> {
  if (!mediaId) {
    return null;
  }

  try {
    // Здесь можно добавить универсальный API для получения медиа по ID
    // Пока что возвращаем null
    return null;
  } catch (error) {
    console.error('Error fetching media:', error);
    return null;
  }
}

/**
 * Получить полный URL для пути к медиа файлу
 */
export function getMediaFilePathUrl(filePath: string | null | undefined): string | null {
  if (!filePath) {
    return null;
  }

  const apiBaseUrl = config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '').replace(/\/$/, '');
  
  if (filePath.startsWith('http')) {
    return filePath;
  }
  
  if (filePath.startsWith('/')) {
    return `${apiBaseUrl}${filePath}`;
  }
  
  return `${apiBaseUrl}/${filePath}`;
}